import React from "react"
import PropTypes from "prop-types"
import draftToHtml from "draftjs-to-html"
import { useHistory } from "react-router-dom"
import { useSelector } from "react-redux"

import styles from "./index.module.scss"
import ResourcePageHeader from "../../components/ResourcePageHeader"
import ResourcePageDetails from "../../components/ResourcePageDetails"
import AttendeesList from "../../components/AttendeesList"
import { resourceTypes } from "../../helpers/helpData"
import Popup from "../../components/Popup/Popup"
import Button from "../../components/Button/Button"
import GoogleMap from "../../components/GoogleMap"
import services from "../../services"
import {
  sanitizeDraftDescription,
  parseNationalResourceDescription,
  linkifyText,
  ensureHtmlLinksOpenInNewTab,
  formatPipeSeparated,
  toTitleCase,
} from "../../helpers/textHelpers"
import {
  contentPurposeOptions,
  resourceFeatureOptions,
} from "../../components/Resource/criteriaOptions"
import { optionLabel } from "../../components/Opportunity/criteriaOptions"

const isHttpUrl = (value) => /^https?:\/\//i.test(value || "")

const ResourcePageContainer = ({ uuid, data }) => {
  const userInfo = useSelector((state) => state.user)

  const history = useHistory()

  const [deletePopupOpened, setDeletePopupOpened] = React.useState(false)

  const [attendeeInfo, setAttendeeInfo] = React.useState({
    isAttendee: data.is_attendee,
    attendees: data.attendees,
    attendeesCount: data.attendees_count,
  })

  const type = resourceTypes.find(
    (item) => item.resourceType === data.resource_type.toLowerCase(),
  )

  const nationalDescription = React.useMemo(
    () => parseNationalResourceDescription(data.description),
    [data.description],
  )

  const descriptionHtml = React.useMemo(() => {
    if (nationalDescription || !data.description) return ""

    try {
      const sanitizedDescription = sanitizeDraftDescription(data.description)
      const parsed =
        typeof sanitizedDescription === "string"
          ? JSON.parse(sanitizedDescription)
          : sanitizedDescription
      const element = document.createElement("div")
      element.innerHTML = draftToHtml(parsed)
      ensureHtmlLinksOpenInNewTab(element)
      return element.innerHTML
    } catch (error) {
      console.error("Failed to render resource description:", error)
      return ""
    }
  }, [data.description, nationalDescription])

  const deleteResource = () => {
    services.resourceServices.deleteResourceByUuid(data.uuid).then(() => {
      history.goBack()
    })
  }

  const onAttend = (status) => {
    services.resourceServices
      .attendToResource({
        uuid: data.uuid,
        status: !attendeeInfo.isAttendee,
      })
      .then((result) => {
        setAttendeeInfo({
          ...attendeeInfo,
          attendeesCount: attendeeInfo.attendeesCount + (status ? 1 : -1),
          attendees: status
            ? [result.data, ...attendeeInfo.attendees]
            : attendeeInfo.attendees.filter(
                (attendee) =>
                  attendee.seeker_uuid !==
                  (userInfo.company.uuid || userInfo.uuid),
              ),
          isAttendee: status,
        })
      })
  }

  return (
    <div className={styles.containerWrap}>
      {deletePopupOpened && (
        <Popup
          dialog
          subtitle="Are you sure you want to cancel your attendance?"
          button={
            <Button buttonStyle="black" onClick={deleteResource}>
              Yes
            </Button>
          }
          acceptButton={
            <Button
              buttonStyle="outlinedBlack"
              onClick={() => {
                setDeletePopupOpened(false)
              }}
            >
              No
            </Button>
          }
          closeAction={() => {
            setDeletePopupOpened(false)
          }}
          isCrossIcon={true}
        />
      )}

      <ResourcePageHeader
        badgeText={type.badgeText}
        badgeBgColor={type.badgeColor}
        bgImg={type.image}
        uuid={data.uuid}
        title={data.topic}
        subTitle={`Company - ${data.company?.name} • ${data.location}`}
        company={data.company}
        onDelete={() => setDeletePopupOpened(true)}
        onAttend={onAttend}
        isAttendee={attendeeInfo.isAttendee}
      />

      <ResourcePageDetails
        data={data}
        websiteUrl={nationalDescription?.websiteUrl}
      />

      <div className={styles.resourceTextContent}>
        {data.description && (
          <React.Fragment>
            <h5>Description</h5>
            {nationalDescription ? (
              <div className={styles.resourceTextItem}>
                {nationalDescription.briefDescription && (
                  <p className={styles.briefDescription}>
                    {linkifyText(nationalDescription.briefDescription)}
                  </p>
                )}
                {!!nationalDescription.meta.length && (
                  <dl className={styles.metaList}>
                    {nationalDescription.meta.map(({ label, value }) => {
                      const formattedValue = formatPipeSeparated(value)
                      const displayValue =
                        label === "Resource Type"
                          ? toTitleCase(formattedValue)
                          : formattedValue

                      return (
                        <div className={styles.metaRow} key={label}>
                          <dt>{label}</dt>
                          <dd>
                            {isHttpUrl(displayValue) ? (
                              <a
                                href={displayValue}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="goldLink"
                              >
                                {displayValue}
                              </a>
                            ) : (
                              linkifyText(displayValue)
                            )}
                          </dd>
                        </div>
                      )
                    })}
                  </dl>
                )}
              </div>
            ) : (
              descriptionHtml && (
                <div
                  className={styles.resourceTextItem}
                  dangerouslySetInnerHTML={{
                    __html: descriptionHtml,
                  }}
                />
              )
            )}
          </React.Fragment>
        )}

        {data.key_benefits && (
          <React.Fragment>
            <h5>Key Beneﬁts:</h5>
            <div className={styles.resourceTextItem}>
              {formatPipeSeparated(data.key_benefits)}
            </div>
          </React.Fragment>
        )}

        {data.who_should_attend && (
          <React.Fragment>
            <h5>Who Should Attend:</h5>
            <div className={styles.resourceTextItem}>
              {formatPipeSeparated(data.who_should_attend)}
            </div>
          </React.Fragment>
        )}

        {data.content_purposes?.length > 0 && (
          <React.Fragment>
            <h5>Content Purpose:</h5>
            <div className={styles.resourceTextItem}>
              {data.content_purposes
                .map((id) => optionLabel(contentPurposeOptions, id) || id)
                .join(", ")}
            </div>
          </React.Fragment>
        )}

        {data.resource_features?.length > 0 && (
          <React.Fragment>
            <h5>Resource Features:</h5>
            <div className={styles.resourceTextItem}>
              {data.resource_features
                .map((id) => optionLabel(resourceFeatureOptions, id) || id)
                .join(", ")}
            </div>
          </React.Fragment>
        )}
      </div>

      <AttendeesList
        companyUuid={uuid}
        attendees={attendeeInfo.attendees}
        attendeesCount={attendeeInfo.attendeesCount}
      />

      <div className={styles.mapArea}>
        <GoogleMap lat={data.lat} lng={data.long} />
      </div>
    </div>
  )
}

ResourcePageContainer.propTypes = {
  uuid: PropTypes.string.isRequired,
  data: PropTypes.shape({
    uuid: PropTypes.string,
  }).isRequired,
}

export default ResourcePageContainer
